import {
  actions,
  Button,
  KeyMap,
  MessageBox,
  Tooltip,
  useCallbackRef,
  useKeymap,
} from '@infotech-soe/erp-ui-kit';
import type {
  ButtonProps,
  MessageBoxButtonProps,
} from '@infotech-soe/erp-ui-kit';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import DatasetService from '../../../services/dataset/DatasetService';
import { PredefinedForms } from '../../../services/form/view-model/FormCreateViewModel';
import { FormSourceType } from '../../../services/form/view-model/FormSource';

import Form from './Form';
import { FormContext } from './FormContext';
import FormControlContainer from './FormControlContainer';
import { TFormDialogConnectorProps } from './FormDialogContainer';
import FormTitle from './FormTitle';
import { useForm } from './useForm';

export interface FormWrapperButton extends Pick<
  MessageBoxButtonProps,
  'disabled' | 'slot' | 'type'
> {
  label: string;
  onClick: () => void;
}

function insertIf<T>(condition: boolean, items: T[]) {
  return condition ? items : [];
}

const messages = defineMessages({
  clear: {
    id: 'ui.clear',
    defaultMessage: 'Clear',
  },
  close: {
    id: 'core.form.close',
    defaultMessage: 'Close',
  },
  prev: {
    id: 'core.form.prev',
    defaultMessage: 'Previous',
  },
  next: {
    id: 'core.form.next',
    defaultMessage: 'Next',
  },
  apply: {
    id: 'core.form.apply',
    defaultMessage: 'Apply',
  },
  cancel: {
    id: 'ui.button.cancel',
    defaultMessage: 'Cancel',
  },
  print: {
    id: 'core.form.print',
    defaultMessage: 'Print',
  },
  showHints: {
    id: 'core.form.showHints',
    defaultMessage: 'Show hints',
  },
  hideHints: {
    id: 'core.form.hideHints',
    defaultMessage: 'Hide hints',
  },
});

const useStyles = createUseStyles({
  dialog: {
    minWidth: 500,
    maxWidth: '98vw',
    maxHeight: '98vh',
  },
  review: {
    minHeight: '150px!important',
  },
  stretchContent: {
    composes: 'col column',
    minHeight: 0,
  },
});

interface HintToggleButtonProps extends Pick<
  ButtonProps,
  'className' | 'onClick'
> {
  hint?: string;
}

const HintToggleButton = ({ hint, ...props }: HintToggleButtonProps) => (
  <Tooltip label={hint}>
    <Button variant="ondark" icon="question-small" {...props} />
  </Tooltip>
);

const FormDialog = (props: TFormDialogConnectorProps): React.JSX.Element => {
  const handleRef = useRef<HTMLDivElement>(null);

  const formState = useForm({
    isSubmitForbidden: props.submitForbidden,
    isSubmitting: props.isSubmitting,
    onBtnSave: props.onBtnSave,
    onSubmit: props.onSubmit,
    onSubmitted: props.onSubmitted,
  });

  useEffect(() => {
    if (handleRef.current) {
      const found = Array.from(
        handleRef.current.querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement
        >('input, textarea')
      ).find(el => !el.disabled && !el.readOnly);

      if (found) {
        found.focus();
      }
    }
  }, []);

  const {
    hasHints,
    areHintsShown,
    id,
    open,
    title,
    controlNames,
    onClear,
    onBtnSave,
    onCancel,
    onToggleHintClick,
    rootControlId,
    formClear,
    formSourceType,
    submitForbidden,
    selectNext,
    selectPrev,
    disabledNext,
    disabledPrev,
    dataset,
    width,
    height,
    formButtons,
    onButtonClick,
  } = props;
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const isButtonDisabled = submitForbidden || formState.errors.size > 0;

  const clear = formatMessage(messages.clear);
  const close = formatMessage(messages.close);
  const prev = formatMessage(messages.prev);
  const next = formatMessage(messages.next);
  const apply = formatMessage(messages.apply);
  const cancel = formatMessage(messages.cancel);
  const print = formatMessage(messages.print);
  const headerButtonHint = formatMessage(
    messages[areHintsShown ? 'hideHints' : 'showHints']
  );

  const applyButton: FormWrapperButton = {
    label: apply,
    disabled: isButtonDisabled,
    type: 'submit' as const,
    onClick: onBtnSave,
  };
  const cancelButton: FormWrapperButton = {
    disabled: false,
    label: cancel,
    onClick: onCancel,
  };
  const clearButton: FormWrapperButton = {
    disabled: false,
    label: clear,
    type: 'reset' as const,
    onClick: onClear,
    slot: 'right',
  };
  const closeButton: FormWrapperButton = {
    disabled: false,
    label: close,
    onClick: onCancel,
  };
  const prevButton: FormWrapperButton = {
    label: prev,
    disabled: disabledPrev,
    onClick: selectPrev,
  };
  const nextButton: FormWrapperButton = {
    label: next,
    disabled: disabledNext,
    onClick: selectNext,
  };

  const modalTrigger = useCallback(() => {
    return {
      focus: () => {
        if (dataset) {
          new DatasetService().focus(dataset);
        }
      },
    };
  }, [dataset]);

  const customButtons: FormWrapperButton[] = useMemo(
    () =>
      formButtons.map(({ label, name, updater, disabled }) => ({
        label,
        onClick: () => onButtonClick(name, updater),
        disabled,
        slot: 'right',
      })),
    [formButtons, onButtonClick]
  );

  const buttons: Record<FormSourceType | PredefinedForms, FormWrapperButton[]> =
    {
      [FormSourceType.CONDITION]: [applyButton, cancelButton, clearButton],
      [FormSourceType.PREDEFINED]: [applyButton, cancelButton],
      [FormSourceType.FORM]: [
        applyButton,
        cancelButton,
        ...customButtons,
        ...insertIf(!!formClear, [clearButton]),
      ],
      [FormSourceType.PRINT]: [
        {
          label: print,
          disabled: isButtonDisabled,
          type: 'submit',
          onClick: onBtnSave,
        },
        cancelButton,
        ...insertIf(!!formClear, [clearButton]),
      ],
      [PredefinedForms.REVIEW]: [prevButton, nextButton, closeButton],
      [FormSourceType.SETTINGS]: [],
      [PredefinedForms.TREE]: [applyButton, cancelButton],
    };

  const hotkeysRef = useKeymap(
    {
      [KeyMap[actions.PREV_OPTION]]: selectPrev,
      [KeyMap[actions.PREV_OPTION_HORIZONTAL]]: selectPrev,
      [KeyMap[actions.NEXT_OPTION]]: selectNext,
      [KeyMap[actions.NEXT_OPTION_HORIZONTAL]]: selectNext,
    },
    {
      enabled: formSourceType === PredefinedForms.REVIEW,
    }
  );

  const callbackRef = useCallbackRef(handleRef, hotkeysRef);

  return (
    <FormContext.Provider value={formState}>
      <MessageBox
        open={open}
        title={<FormTitle title={title} />}
        headerButtons={
          hasHints ? (
            <HintToggleButton
              hint={headerButtonHint}
              onClick={onToggleHintClick}
            />
          ) : undefined
        }
        onClose={onCancel}
        noCloseButton={false}
        className={classes.dialog}
        height={height ?? 'auto'}
        resizable
        onRef={callbackRef}
        modalTrigger={modalTrigger}
        width={width}
        {...(formSourceType === PredefinedForms.REVIEW && {
          y: '20%',
        })}
      >
        <MessageBox.Content
          className={classNames(classes.stretchContent, {
            [classes.review]: formSourceType === PredefinedForms.REVIEW,
          })}
        >
          {!rootControlId ? (
            controlNames.map(name => (
              <div key={name} className="m-ma-sm">
                <FormControlContainer name={name} formId={id} />
              </div>
            ))
          ) : (
            <Form id={id} rootControlId={rootControlId} />
          )}
        </MessageBox.Content>
        <MessageBox.Footer>
          {buttons[formSourceType].map(({ label, ...btnProps }) => (
            <MessageBox.Button key={label} label={label} {...btnProps} />
          ))}
        </MessageBox.Footer>
      </MessageBox>
    </FormContext.Provider>
  );
};

export default FormDialog;
